import type { Callback, Query, RequestOptions, Response } from "mixpanel-browser";
import { usePathname } from "next/navigation";
import { useCallback, useMemo, useRef } from "react";
import { useReactiveVar } from "@apollo/client/react";

import { useMixpanelContext } from "@portal/app/_components/context/MixpanelProvider";
import { decodeJwt, TokenInfo } from "@portal/app/_common/lib/token";
import { accessTokenVar } from "../apolloUtils";
import { getClientExperiments, toFriendlyExperiments } from "@portal/app/_common/lib/experiments";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { isTrackableVisitor } from "@portal/app/_common/lib/isTrackableVisitor";
import {
  MixpanelEventName,
  MIXPANEL_ADMIN_BYPASS_EVENTS,
} from "@portal/app/_common/types/mixpanel";

export type SafeTrack = {
  (
    eventName: MixpanelEventName,
    properties?: Record<string, unknown>,
    optionsOrCallback?: RequestOptions | Callback,
    callback?: Callback
  ): Promise<void>;
};

export type SafeTrackLinks = {
  (query: Query, eventName: MixpanelEventName, properties?: Record<string, unknown>): void;
};

const getDefaultEventProperties = (customerId: string | undefined) => {
  return {
    // Capture at invocation, before a loading SDK or route transition can change it.
    ...(typeof window !== "undefined" && { pathname: window.location.pathname }),
    ...(customerId && {
      customerId,
      experiments: toFriendlyExperiments(getClientExperiments()),
    }),
    client_version: Number(process.env.DREAM_CLIENT_VERSION),
  };
};

export const useMixpanel = () => {
  const accessToken = useReactiveVar(accessTokenVar);
  const path = usePathname();
  const calledTrackPageRef = useRef(false);
  const calledTrackLinksRef = useRef<Query[]>([]);
  const mixpanel = useMixpanelContext();
  const {
    clientLocation: { country },
  } = usePageContext();
  const { customerId, activeRole } = useMemo(
    () => decodeJwt<TokenInfo>(accessToken) ?? ({} as TokenInfo),
    [accessToken]
  );

  const shouldTrack = useCallback(
    (eventName: MixpanelEventName) =>
      isTrackableVisitor({
        activeRole,
        country,
        allowAdmin: MIXPANEL_ADMIN_BYPASS_EVENTS.includes(eventName),
      }),
    [activeRole, country]
  );

  if (!mixpanel) {
    throw new Error("Can only be used under Mixpanel context");
  }

  const track: SafeTrack = useCallback(
    async (eventName, properties, optionsOrCallback, callback) => {
      if (!shouldTrack(eventName)) {
        return;
      }
      return new Promise((resolve) =>
        mixpanel.track(
          eventName,
          {
            ...properties,
            ...getDefaultEventProperties(customerId),
          },
          ...(callback
            ? [
                optionsOrCallback as RequestOptions,
                (response: Response) => {
                  callback(response);
                  resolve();
                },
              ]
            : typeof optionsOrCallback === "function"
              ? [
                  (response: Response) => {
                    optionsOrCallback(response);
                    resolve();
                  },
                ]
              : [(optionsOrCallback as RequestOptions) ?? (() => resolve())])
        )
      );
    },
    [customerId, mixpanel, shouldTrack]
  );

  const trackOnePageView: SafeTrack = useCallback(
    (eventName, properties, optionsOrCallback, callback) => {
      if (!shouldTrack(eventName) || calledTrackPageRef.current || typeof window === "undefined") {
        return Promise.resolve();
      }

      calledTrackPageRef.current = true;
      return track(
        eventName ?? "Page Viewed",
        {
          path,
          ...properties,
          ...getDefaultEventProperties(customerId),
        },
        optionsOrCallback,
        callback
      );
    },
    [customerId, path, shouldTrack, track]
  );

  /**
   * If the link directs outside of dreambound.com, mixpanel will miss the event
   * when using `track`. Using track_links is able to track these events.
   * An example of this is with clickouts / pre-lead redirects.
   */
  const trackLinks: SafeTrackLinks = useCallback(
    async (query, eventName, properties) => {
      if (
        !shouldTrack(eventName) ||
        calledTrackLinksRef.current.includes(query) ||
        typeof window === "undefined"
      ) {
        return Promise.resolve();
      }

      calledTrackLinksRef.current.push(query);
      return mixpanel.track_links(query, eventName, {
        ...properties,
        ...getDefaultEventProperties(customerId),
      });
    },
    [customerId, mixpanel, shouldTrack]
  );

  return useMemo(
    () => ({ provider: mixpanel, trackOnePageView, track, trackLinks }),
    [mixpanel, track, trackLinks, trackOnePageView]
  );
};
